import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const scrollContainerSelectors = [
  'main.app-shell',
  '.app-shell',
  '.operator-page',
  '.dashboard-container',
  '.settings-page__layout',
  '.settings-page__canvas',
  '[data-route-scroll-root]',
].join(',');

const scrollableOverflowPattern = /(auto|scroll|overlay)/;

const getScrollTargets = (): HTMLElement[] => {
  const targets = new Set<HTMLElement>([
    document.documentElement,
    document.body,
  ]);

  if (document.scrollingElement instanceof HTMLElement) {
    targets.add(document.scrollingElement);
  }

  document
    .querySelectorAll<HTMLElement>(scrollContainerSelectors)
    .forEach((target) => targets.add(target));

  document.querySelectorAll<HTMLElement>('body *').forEach((target) => {
    const style = window.getComputedStyle(target);
    const canScrollVertically = scrollableOverflowPattern.test(style.overflowY)
      && target.scrollHeight > target.clientHeight;
    const canScrollHorizontally = scrollableOverflowPattern.test(style.overflowX)
      && target.scrollWidth > target.clientWidth;

    if (canScrollVertically || canScrollHorizontally) {
      targets.add(target);
    }
  });

  return Array.from(targets);
};

export const resetScrollPosition = () => {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

  getScrollTargets().forEach((target) => {
    target.scrollTop = 0;
    target.scrollLeft = 0;
  });
};

export const scheduleScrollPositionReset = () => {
  resetScrollPosition();

  let secondFrameId: number | undefined;
  const firstFrameId = window.requestAnimationFrame(() => {
    resetScrollPosition();
    secondFrameId = window.requestAnimationFrame(() => {
      resetScrollPosition();
    });
  });

  const timeoutId = window.setTimeout(() => {
    resetScrollPosition();
  }, 120);
  const delayedTimeoutIds = [240, 480, 900].map((delayMs) => window.setTimeout(() => {
    resetScrollPosition();
  }, delayMs));

  return () => {
    window.cancelAnimationFrame(firstFrameId);

    if (secondFrameId !== undefined) {
      window.cancelAnimationFrame(secondFrameId);
    }

    window.clearTimeout(timeoutId);
    delayedTimeoutIds.forEach((delayedTimeoutId) => window.clearTimeout(delayedTimeoutId));
  };
};

const clearStaleModalLayers = () => {
  // Route transitions should never leave behind body lock styles from a closed modal.
  // Do not manually remove backdrop nodes; react-bootstrap owns that lifecycle.
  if (document.querySelector('.modal.show')) {
    return;
  }

  document.body.classList.remove('modal-open');
  document.body.style.removeProperty('overflow');
  document.body.style.removeProperty('padding-right');
};

const RouteScrollReset: React.FC = () => {
  const location = useLocation();
  const routeKey = `${location.pathname}${location.search}${location.hash}:${location.key}`;
  const previousRouteKeyRef = useRef(routeKey);

  useEffect(() => {
    const { history } = window;
    const previousRestoration = history.scrollRestoration;

    history.scrollRestoration = 'manual';

    return () => {
      history.scrollRestoration = previousRestoration;
    };
  }, []);

  useLayoutEffect(() => {
    if (previousRouteKeyRef.current === routeKey) {
      return undefined;
    }

    previousRouteKeyRef.current = routeKey;

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    clearStaleModalLayers();
    const cancelScrollReset = scheduleScrollPositionReset();
    const frameId = window.requestAnimationFrame(() => {
      clearStaleModalLayers();
    });

    return () => {
      cancelScrollReset();
      window.cancelAnimationFrame(frameId);
    };
  }, [routeKey]);

  return null;
};

export default RouteScrollReset;
